import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState, useCallback } from 'react';

import { CityDto } from '@cineamo/api/models/city/CityDto.types';

import { CityListResponse } from '@cineamo/api/types/ApiListResponse.types';
import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { getLocation } from '@cineamo/ui/src/helpers/location-helper';

import { useDebouncedState } from '@cineamo/ui/src/hooks/useDebouncedState';

import { IconFilter24 } from '@cineamo/ui/src/icons/ic-filter';
import { IconPosition24 } from '@cineamo/ui/src/icons/ic-position';
import { IconSearch24 } from '@cineamo/ui/src/icons/ic-search';

import { usePagination } from '../hooks/usePagination';
import { useAnalytics } from '../providers/analyticsProvider';
import useLocationStore from '../stores/location/useLocationStore';

import type { SearchCitiesParams } from './actions';
import SearchBarAutocomplete from './SearchBarAutocomplete';

type SearchMode = 'geo' | 'manual' | 'ip';

const SearchBar: React.FC<{
    viewType: 'list' | 'map';
    isFilterable: boolean;
    searchCitiesAction: (params: SearchCitiesParams) => Promise<ApiResponse<unknown, CityListResponse>>;
    // Opens the host-rendered filter panel — every current host provides this.
    onOpenFilter?: () => void;
    activeFilterCount?: number;
}> = ({ viewType, isFilterable, searchCitiesAction, onOpenFilter, activeFilterCount = 0 }) => {
    const t = useTranslations();
    const { setLocation, setGeoLocation, latitude, longitude, geoLocation } = useLocationStore();
    const userCountryIso2 = geoLocation?.country ?? 'DE';
    const getCityDisplayName = useCallback(
        (city: CityDto) =>
            city.iso31662?.toLowerCase() === userCountryIso2.toLowerCase() && city.nameLocal
                ? city.nameLocal
                : city.name,
        [userCountryIso2]
    );
    const { track } = useAnalytics();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMode, setSearchMode] = useState<SearchMode>('ip');

    // Separate local input state from parent searchQuery
    const [inputValue, setInputValue] = useState('');
    const [showAutocomplete, setShowAutocomplete] = useState(false);

    // Debounce input for manual search
    const debouncedInputValue = useDebouncedState(inputValue, 300);

    // Query for geo/ip modes - find city by coordinates only
    const { data: cityByCoords, isFetching: isFetchingCityByCoords } = useQuery({
        queryKey: ['city-by-coords', latitude, longitude, geoLocation?.country],
        queryFn: async () => {
            const response = await searchCitiesAction({
                latitude,
                longitude,
                ...(geoLocation?.country ? { iso31662: geoLocation.country, includeCinemaCountries: true } : {})
            });

            return response?._embedded?.cities?.[0] ?? null;
        },
        enabled: Boolean(latitude && longitude && ['geo', 'ip'].includes(searchMode)),
        staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    });

    // Query for manual mode - infinite scroll search cities by name
    const {
        data: citiesData,
        hasNextPage,
        isLoading: isLoadingCities,
        isFetchingNextPage,
        ref: loaderRef
    } = usePagination<CityListResponse>(
        ['cities-by-query-pagination', debouncedInputValue, latitude, longitude, geoLocation?.country],
        async (page = 1) => {
            const response = await searchCitiesAction({
                query: debouncedInputValue,
                iso31662: geoLocation?.country,
                includeCinemaCountries: true,
                page
            });

            return response;
        },
        {
            enabled: Boolean(debouncedInputValue && searchMode === 'manual' && debouncedInputValue.length >= 2),
            staleTime: 1 * 60 * 1000 // Cache for 1 minute
        }
    );

    // Flatten paginated cities into single array
    const citiesByQuery = citiesData?.pages?.flatMap((page) => page?._embedded?.cities ?? []) ?? [];

    // Handle geo location button click
    useEffect(() => {
        if (searchMode === 'geo') {
            track('GPS Location Button Clicked');
            getLocation(
                (location) => {
                    const latitude = location.latitude;
                    const longitude = location.longitude;
                    setLocation(longitude, latitude);
                },
                () => {}
            );
        }
    }, [searchMode, setLocation, track]);

    // Update input when geo/ip mode resolves a city
    useEffect(() => {
        if (!cityByCoords) {
            return;
        }
        const displayName = getCityDisplayName(cityByCoords);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: populate input from async geo/ip city lookup
        setInputValue(displayName);
        setSearchQuery(displayName);
        setGeoLocation({
            longitude: cityByCoords.longitude,
            latitude: cityByCoords.latitude,
            city: displayName,
            country: cityByCoords.iso31662 ?? geoLocation?.country
        });
        setShowAutocomplete(false);
    }, [
        cityByCoords,
        isFetchingCityByCoords,
        searchMode,
        setSearchQuery,
        setGeoLocation,
        geoLocation?.country,
        getCityDisplayName
    ]);

    // Handle city selection from autocomplete
    const handleSelectCity = (city: CityDto) => {
        const displayName = getCityDisplayName(city);
        track('City From Autocomplete Selected', {
            city: displayName
        });
        setInputValue(displayName);
        setSearchQuery(displayName);
        setLocation(city.longitude, city.latitude);
        setGeoLocation({
            longitude: city.longitude,
            latitude: city.latitude,
            city: displayName,
            country: city.iso31662 ?? geoLocation?.country
        });
        setShowAutocomplete(false);
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setSearchQuery(value);

        // Switch to manual mode when user starts typing
        if (searchMode !== 'manual') {
            track('City Input Typing Started');
            setSearchMode('manual');
        }

        // Show autocomplete if there's enough input
        setShowAutocomplete(value.length >= 2);
    };

    // Sync input with parent searchQuery on mount or mode change from parent
    useEffect(() => {
        if (searchQuery !== inputValue && searchMode !== 'manual') {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync local input with parent searchQuery when not in manual mode
            setInputValue(searchQuery);
        }
    }, [searchQuery, searchMode, inputValue]);

    // Only show skeleton when loading coordinates for geo/ip (not during manual typing)
    const showSkeleton = isFetchingCityByCoords && ['geo', 'ip'].includes(searchMode);

    return (
        <div
            className={classnames(
                'grid transition-[grid-template-rows,opacity] duration-300',
                // On mobile: collapse when map view
                viewType === 'list' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                // On desktop (lg): always show (grid-rows-[1fr] opacity-100)
                'lg:grid-rows-[1fr] lg:opacity-100'
            )}>
            <div className="w-full px-12 py-6">
                <div className="relative">
                    <div className="flex flex-row space-x-6">
                        <div className="w-full h-full bg-[var(--search-background-color)] text-[var(--search-on-background-color)] rounded-4 flex flex-row">
                            <button
                                className="p-12"
                                onClick={() => {
                                    setSearchMode('geo');
                                }}>
                                <IconPosition24 className="w-24 h-24" />
                            </button>
                            <div className="p-12 border-l-2 border-[var(--surface-color)] flex flex-row space-x-10 flex-1">
                                <div className="flex items-center">
                                    <IconSearch24 className="w-24 h-24" />
                                </div>
                                {showSkeleton ? (
                                    <div className="w-[200px] h-24 animate-pulse bg-[var(--search-on-background-color)] opacity-20 rounded-4" />
                                ) : (
                                    <input
                                        type="text"
                                        placeholder={t('showtimes-finder.search.placeholder')}
                                        className="grow bg-transparent outline-none border-none p-0 leading-[24px] h-[24px] text-body-14 ![--tw-ring-color:transparent] font-bold placeholder:font-normal"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        onFocus={() => {
                                            if (searchMode === 'manual' && inputValue.length >= 2) {
                                                setShowAutocomplete(true);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        {isFilterable && (
                            <button
                                className="relative bg-[var(--search-background-color)] text-[var(--search-on-background-color)] rounded-4 p-12"
                                onClick={() => onOpenFilter?.()}>
                                <IconFilter24 className="w-24 h-24" />
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-4 -right-4 min-w-[20px] h-20 px-4 rounded-16 bg-[var(--primary-color)] text-[var(--on-primary-color)] text-body-bold-10 flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                    <SearchBarAutocomplete
                        cities={citiesByQuery}
                        userCountryIso2={userCountryIso2}
                        isLoading={isLoadingCities}
                        isFetchingNextPage={isFetchingNextPage}
                        hasNextPage={hasNextPage !== false}
                        onSelectCity={handleSelectCity}
                        loaderRef={loaderRef}
                        show={showAutocomplete && searchMode === 'manual'}
                    />
                </div>
            </div>
        </div>
    );
};

export default SearchBar;
